import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "./cn";
import { Spinner } from "./spinner";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl font-bold whitespace-nowrap transition-colors select-none disabled:cursor-not-allowed [&_svg]:size-[18px] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-on-primary hover:bg-primary-hover disabled:bg-neutral-soft disabled:text-faint",
        secondary:
          "border border-border-strong bg-surface text-ink hover:bg-surface-muted disabled:text-faint",
        quiet: "text-link hover:bg-primary-soft disabled:text-faint",
        destructive:
          "border border-danger-border bg-surface text-danger hover:bg-danger-soft disabled:text-faint",
        /** The confirm button of a change that can't be undone. */
        danger:
          "bg-danger-solid text-on-danger-solid hover:bg-danger-solid-hover disabled:bg-neutral-soft disabled:text-faint",
      },
      size: {
        sm: "h-9 px-3 text-[13px]",
        md: "h-11 px-[18px] text-sm",
        lg: "h-12 px-5 text-[15px]",
        icon: "size-11",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner and blocks clicks (the label stays, so the button doesn't jump). */
  loading?: boolean;
  /** Renders the child element (e.g. a router Link) with button styles. */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  loading = false,
  asChild = false,
  disabled,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  if (asChild) {
    return (
      <Slot.Root className={cn(buttonVariants({ variant, size }), className)} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
}
